import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { ReplaceStep } from "@tiptap/pm/transform";
import { ySyncPluginKey } from "@tiptap/y-tiptap";

import { openSelectionSuggestions } from "@/components/tiptap/context-menu/utils";
import { getInputPosition } from "@/components/tiptap/utils/getInputPosition";
import { isOpenSuggestionsShortcut } from "@/utils/hotkeys";
import { trackEvent } from "@/utils/trackEvent";

import type { DocumentModel } from "@/models/DocumentModel";

export const SuggestPrompt = Extension.create<{ doc: DocumentModel }>({
  name: "suggestPrompt",

  onTransaction({ transaction, editor }) {
    const { state } = editor;
    const { doc } = this.options;

    if (transaction.getMeta("phantomHint") !== undefined) {
      return;
    }

    if (doc.cursor.isActive) {
      return;
    }

    const [start, end, value] = getInputPosition(state, false);

    const { from: prevStart, to: prevEnd } = doc.input.inputPosRange;

    if (value !== doc.input.value) {
      // allow to show suggestions when user adds new content.
      // if suggestions are hidden and user only removed content (with Backspace/Delete or otherwise),
      // it should keep siggestions hidden
      const hasAdditions = transaction.steps.some(
        (step) => step instanceof ReplaceStep && step.slice.size > 0,
      );
      const { isUndoRedoOperation } =
        (transaction.getMeta(ySyncPluginKey) as Record<string, boolean>) || {};
      if (hasAdditions && !transaction.getMeta("implicit") && !isUndoRedoOperation) {
        doc.editor.suggestions.mode = "active";
      }
      if (
        transaction.docChanged ||
        (!doc.editor.suggestions.visible && !doc.editor.suggestions.pointerAnchor)
      ) {
        doc.input.setValue(value);
      }

      // when user continue typing text that doesn't have suggestions, we are in "ephemeral" state
      // that would show suggestions again if user removes typed text.
      // For example, user types "gammaXXXXXXX" and then removes "XXXXXXX" - in this case
      // we should show suggestions. In a sense, user is in the editing state while they only
      // typing or removing text.
      // However, cursor navigation should break this state - we hide suggestions until user types in
      // new text or presses Ctrl+Space.

      if (
        !transaction.docChanged &&
        !doc.editor.suggestions.visible &&
        doc.editor.suggestions.mode !== "idle" &&
        !doc.editor.suggestions.pointerAnchor
      ) {
        doc.editor.suggestions.mode = "idle";
      }
    }

    if (!transaction.docChanged) {
      if (
        doc.editor.suggestions.visible &&
        !doc.editor.editorState.latexInput &&
        !doc.editor.suggestions.pointerAnchor
      ) {
        if (prevStart > start || prevEnd < end) {
          doc.editor.suggestions.reset();
        }
      }
      return;
    }

    // FIXME: this can lead to a new transaction - it should probably be the same transaction
    // calling it without queueMicrotask leads to react state update errors in tiptap
    queueMicrotask(() => {
      doc.input.setInputPosRange({ from: start, to: end });
    });
  },

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin({
        props: {
          handleKeyDown(view, e) {
            if (doc.cursor.isActive) {
              return;
            }

            if (isOpenSuggestionsShortcut(e)) {
              e.preventDefault();

              const { selection } = view.state;

              if (!selection.empty) {
                const coords = view.coordsAtPos(selection.from);
                const context = openSelectionSuggestions(doc, view, coords.left, coords.bottom);

                trackEvent("suggestions_opened", {
                  context,
                  source: "shortcut",
                  doc_id: doc.id,
                });

                return true;
              }

              if (doc.editor.suggestions.available) {
                const [start, end, value] = getInputPosition(view.state, true);

                doc.input.setValue(value);
                doc.input.setInputPosRange({ from: start, to: end }, true);
                doc.editor.suggestions.open("caret");
                trackEvent("suggestions_opened", {
                  context: "caret",
                  source: "shortcut",
                  doc_id: doc.id,
                });
              }
            }
          },
        },
      }),
    ];
  },
});
